import {
  Activity, AlertTriangle, Gauge, ListChecks, Percent, ShieldCheck, ShieldAlert, RefreshCw, TrendingUp,
} from 'lucide-react';
import { useReliabilityMetrics, type ReliabilityMetrics as Metrics } from '../lib/data';
import {
  Skeleton,
  SkeletonChart,
  SkeletonBarRow,
} from './Skeleton';

function MetricCard({ icon: Icon, label, value, sub, tone }: {
  icon: typeof Activity; label: string; value: string | number; sub?: string; tone: string;
}) {
  return (
    <div className="rounded-lg border p-4 flex items-center gap-3" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
      <div className="p-2.5 rounded-md" style={{ background: 'var(--ink-faint)', color: tone }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-secondary)' }}>{label}</div>
        <div className="text-xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
        {sub && <div className="text-[10px]" style={{ color: 'var(--ink-secondary)' }}>{sub}</div>}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }: { title: string; icon: typeof Activity; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
      <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
        <Icon className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
        <h3 className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** Inline-SVG line chart of review scores (0–100) over time. No chart lib. */
function ScoreTrend({ points }: { points: { ts: string; score: number }[] }) {
  const W = 520;
  const H = 120;
  const PAD = 8;
  if (points.length === 0) return <Empty text="No reviews yet." />;
  const xs = points.length === 1 ? [W / 2] : points.map((_, i) => PAD + (i * (W - 2 * PAD)) / (points.length - 1));
  const ys = points.map((p) => H - PAD - (p.score / 100) * (H - 2 * PAD));
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${d} L${xs[xs.length - 1].toFixed(1)},${H - PAD} L${xs[0].toFixed(1)},${H - PAD} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
      <defs>
        <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1={PAD} x2={W - PAD} y1={PAD + g * (H - 2 * PAD)} y2={PAD + g * (H - 2 * PAD)} stroke="var(--border)" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#scoreFill)" />
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="2" fill="var(--accent)" />)}
    </svg>
  );
}

function BarList({ items, emptyText }: { items: { label: string; count: number }[]; emptyText: string }) {
  if (items.length === 0) return <Empty text={emptyText} />;
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-3">
          <div className="w-28 truncate text-xs font-mono" style={{ color: 'var(--ink-secondary)' }}>{it.label}</div>
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--ink-faint)' }}>
            <div className="h-full rounded-full" style={{ width: `${(it.count / max) * 100}%`, background: 'var(--accent)' }} />
          </div>
          <div className="w-6 text-right text-xs font-mono" style={{ color: 'var(--ink)' }}>{it.count}</div>
        </div>
      ))}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-8 text-center text-xs" style={{ color: 'var(--ink-secondary)' }}>{text}</div>;
}

export function Reliability() {
  const { metrics, loading, error, retry } = useReliabilityMetrics();
  const m: Metrics | null = metrics;

  if (loading) {
    return (
      <div className="space-y-5" aria-busy="true" aria-label="Loading reliability metrics">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="rounded-lg border p-4 flex items-center gap-3"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
            >
              <Skeleton width={32} height={32} rounded={6} />
              <div className="space-y-2 min-w-0 flex-1">
                <Skeleton width="60%" height={10} rounded={3} />
                <Skeleton width="40%" height={20} rounded={4} />
                <Skeleton width="80%" height={10} rounded={3} />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
              <Skeleton width={14} height={14} rounded={3} />
              <Skeleton width={140} height={11} rounded={3} />
            </div>
            <div className="p-4 space-y-3">
              <SkeletonChart height={128} />
              <Skeleton width={120} height={9} rounded={3} />
            </div>
          </div>
          <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
              <Skeleton width={14} height={14} rounded={3} />
              <Skeleton width={150} height={11} rounded={3} />
            </div>
            <div className="p-4 space-y-3">
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
            </div>
          </div>
          <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
              <Skeleton width={14} height={14} rounded={3} />
              <Skeleton width={140} height={11} rounded={3} />
            </div>
            <div className="p-4 space-y-3">
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
            </div>
          </div>
          <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
              <Skeleton width={14} height={14} rounded={3} />
              <Skeleton width={130} height={11} rounded={3} />
            </div>
            <div className="p-4 space-y-3">
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
              <SkeletonBarRow />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border p-8 flex flex-col items-center text-center" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
        <ShieldAlert className="w-6 h-6 mb-2" style={{ color: '#dc2626' }} />
        <div className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>Couldn't load reliability data</div>
        <div className="mt-1 max-w-md break-words font-mono text-[11px]" style={{ color: 'var(--ink-secondary)' }}>{error}</div>
        <button
          onClick={retry}
          disabled={loading}
          className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border disabled:opacity-50"
          style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}
        >
          <RefreshCw className="w-3.5 h-3.5" /> Retry
        </button>
      </div>
    );
  }

  if (!m) {
    return <Empty text="Reliability data unavailable. Is the Worker reachable?" />;
  }

  const defectPct = Math.round(m.defectRate * 100);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={ListChecks} label="Total reviews" value={m.totalReviews} tone="var(--accent)" />
        <MetricCard icon={Percent} label="Defect rate" value={`${defectPct}%`} sub={`${m.defectCount} not approved`} tone="#b45309" />
        <MetricCard icon={AlertTriangle} label="Findings / PR" value={m.findingsPerPr.toFixed(1)} sub={`${m.totalFindings} total`} tone="#dc2626" />
        <MetricCard icon={Gauge} label="Avg score" value={m.avgScore} sub="/ 100" tone="#059669" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Review score trend" icon={TrendingUp}>
          <ScoreTrend points={m.scoreTrend} />
          <div className="mt-2 text-[10px] font-mono" style={{ color: 'var(--ink-secondary)' }}>
            last {m.scoreTrend.length} review{m.scoreTrend.length === 1 ? '' : 's'}
          </div>
        </Card>

        <Card title="Findings by category" icon={Activity}>
          <BarList
            items={m.categoryCounts.map((c) => ({ label: c.category, count: c.count }))}
            emptyText="No categorized findings yet."
          />
        </Card>

        <Card title="Findings by severity" icon={ShieldCheck}>
          <BarList
            items={m.severityCounts.map((s) => ({ label: s.severity, count: s.count }))}
            emptyText="No severity data yet."
          />
        </Card>

        <Card title="Top flagged files" icon={AlertTriangle}>
          <BarList
            items={m.topFiles.map((f) => ({ label: f.filepath, count: f.count }))}
            emptyText="No file-level findings yet."
          />
        </Card>
      </div>
    </div>
  );
}
